import * as THREE from "three";
import { Line2 } from "three/examples/jsm/lines/Line2.js";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { Emitter } from "./emitters";
import { BUTTON_BASE_RADIUS, displayPanelHalfExtent, displayPanelHalfExtentFor, displaySizePatch, ENVELOPE_BASE_HALF, isCollider, KNOB_BASE_RADIUS, LAMP_BASE_RADIUS, PITCH_QUANTIZE_BASE_HALF, PlacedObject, seqBaseHalfExtent, seqScale, seqUnitScalePatch, SEQ_FRAME_W, widgetScale, WIDGET_MAX_UNITS, WIDGET_SCALE_MAX, WIDGET_SCALE_MIN } from "./objects";
import { quantize, snapPos } from "./snap";
import { applyFatLineResolution, fatLineMaterial } from "./fatLine";

const EMITTER_GIZMO_BOUND = 0.9;

const RING_MARGIN = 0.5;
const RING_TUBE = 0.06;
const STUB_ARC = Math.PI / 6;
const STUB_ANGLE = -Math.PI / 4;
const STUB_PICK_THICK = 0.32;
const STUB_PICK_MARGIN = 0.2;
const HANDLE_SIZE = 0.24;
const HANDLE_HALF = HANDLE_SIZE / 2;
const MIN_HALF = 0.2;
const MIN_WALL_HALF = 0.05;
const SNAP_STEP_RAD = Math.PI / 12;
const SNAP_DOT_RADIUS_FACTOR = 2;
const SNAP_LINE_LEN_FACTOR = 4.4;
const SNAP_DOT_SIZE = 0.07;
const SNAP_DOT_ACTIVE_COLOR = 0xffffff;
const SNAP_DOT_ACTIVE_SCALE = 1.6;

type Axis = "x" | "y" | "z";

/**
 * Ring axes extend the world axes with "roll": a ring about a target-specific
 * world direction (the rail's long axis), shown only in 3D so the rail's plane
 * can be banked. Scale handles stay world-axis only.
 */
export type RingAxis = Axis | "roll";

const AXIS_COLOR: Record<RingAxis, number> = { x: 0xff5566, y: 0x55dd66, z: 0x5599ff, roll: 0xf4e26a };

const AXIS_UNIT: Record<Axis, THREE.Vector3> = {
  x: new THREE.Vector3(1, 0, 0),
  y: new THREE.Vector3(0, 1, 0),
  z: new THREE.Vector3(0, 0, 1),
};

const AXIS_UV: Record<Axis, { u: THREE.Vector3; v: THREE.Vector3 }> = {
  x: { u: new THREE.Vector3(0, 1, 0), v: new THREE.Vector3(0, 0, 1) },
  y: { u: new THREE.Vector3(0, 0, 1), v: new THREE.Vector3(1, 0, 0) },
  z: { u: new THREE.Vector3(1, 0, 0), v: new THREE.Vector3(0, 1, 0) },
};

export type GizmoHandle =
  | { kind: "rotate"; axis: RingAxis }
  | { kind: "scale"; index: number }
  | { kind: "railPoint"; index: number };

/**
 * Per-handle corner signs for the box resize handles, index-aligned with the
 * `scale` GizmoHandle. Only the two diagonally opposite +Z-face corners carry a
 * handle (top-right and bottom-left), matching the seq widgets; each diagonal-
 * fixes to its opposite corner so a single drag still resizes every axis. A zero
 * z-sign marks a 2D handle: it rides the +Z face corner visually but its drag
 * leaves the depth half-extent and center Z untouched.
 */
const BOX_CORNER_SIGNS_2D: ReadonlyArray<readonly [number, number, number]> = [
  [1, 1, 0],
  [-1, -1, 0],
];

const BOX_CORNER_SIGNS_3D: ReadonlyArray<readonly [number, number, number]> = [
  [1, 1, 1],
  [-1, -1, 1],
];

function boxCornerSigns(is3D: boolean): ReadonlyArray<readonly [number, number, number]> {
  return is3D ? BOX_CORNER_SIGNS_3D : BOX_CORNER_SIGNS_2D;
}

/** The two diagonally opposite corners (top-right, bottom-left) the seq / envelope resize handles ride, index-aligned with the `scale` GizmoHandle. */
const SEQ_CORNER_SIGNS_2D: ReadonlyArray<readonly [number, number]> = [
  [1, 1],
  [-1, -1],
];

/**
 * Outer half-extents of a seq / envelope widget at its current scale — the
 * corner the resize handle rides. Array / curve carry their scale in params.x/y
 * over a base extent plus the frame border; envelope scales ENVELOPE_BASE_HALF
 * by its dedicated scale field.
 */
function seqOuterHalf(obj: PlacedObject): { hw: number; hh: number } {
  if (obj.type === "display") {
    return displayPanelHalfExtent(obj);
  }
  if (obj.type === "envelope") {
    return { hw: ENVELOPE_BASE_HALF.x * obj.scale.x, hh: ENVELOPE_BASE_HALF.y * obj.scale.y };
  }
  if (obj.type === "pitchQuantize") {
    return { hw: PITCH_QUANTIZE_BASE_HALF.x * obj.scale.x, hh: PITCH_QUANTIZE_BASE_HALF.y * obj.scale.y };
  }
  const base = seqBaseHalfExtent(obj);
  const s = seqScale(obj);
  return { hw: base.w * s.x + SEQ_FRAME_W, hh: base.h * s.y + SEQ_FRAME_W };
}

/**
 * Inverse of seqOuterHalf: maps a dragged outer half-extent back to the type's
 * scale-storage patch, clamped to the widget scale range. Array / curve write
 * params.x/y; envelope writes its scale field.
 */
function seqScalePatch(obj: PlacedObject, hw: number, hh: number): { params?: THREE.Vector4; scale?: THREE.Vector2 } {
  const clamp = (v: number): number => Math.min(Math.max(v, WIDGET_SCALE_MIN), WIDGET_SCALE_MAX);
  if (obj.type === "display") {
    const { wUnits, hUnits } = displaySizePatch(hw, hh);
    return { params: new THREE.Vector4(wUnits, hUnits, 0, 0) };
  }
  if (obj.type === "envelope") {
    return { scale: new THREE.Vector2(clamp(hw / ENVELOPE_BASE_HALF.x), clamp(hh / ENVELOPE_BASE_HALF.y)) };
  }
  if (obj.type === "pitchQuantize") {
    return { scale: new THREE.Vector2(clamp(hw / PITCH_QUANTIZE_BASE_HALF.x), clamp(hh / PITCH_QUANTIZE_BASE_HALF.y)) };
  }
  const patch = seqUnitScalePatch(obj, hw, hh);
  return { params: new THREE.Vector4(patch.x, patch.y, 0, 0) };
}

/**
 * The outer half-extent a `seqScalePatch` result actually realizes, which differs
 * from the dragged half-extent when the patch quantizes (count's integer digit
 * count) or clamps (widget scale range). Driving the resize position off this
 * keeps the opposite (fixed) corner pinned: a count snaps in whole-digit steps on
 * the dragged side while the far corner never drifts.
 */
function realizedHalf(obj: PlacedObject, patch: { params?: THREE.Vector4; scale?: THREE.Vector2 }): { hw: number; hh: number } {
  if (obj.type === "display" && patch.params) {
    return displayPanelHalfExtentFor(patch.params.x, patch.params.y);
  }
  if ((obj.type === "array" || obj.type === "curve") && patch.params) {
    const base = seqBaseHalfExtent(obj);
    return { hw: base.w * patch.params.x + SEQ_FRAME_W, hh: base.h * patch.params.y + SEQ_FRAME_W };
  }
  if (patch.scale) {
    const base = obj.type === "envelope" ? ENVELOPE_BASE_HALF : PITCH_QUANTIZE_BASE_HALF;
    return { hw: base.x * patch.scale.x, hh: base.y * patch.scale.y };
  }
  return seqOuterHalf(obj);
}

/**
 * The minimal shape the gizmo grabs: a live position + orientation, a radius to
 * size the rings/handles outside the body, and whether scale handles apply.
 * `position`/`rotation` are held by reference so a drag writing through them is
 * reflected immediately. There is no base class — PlacedObject and Emitter each
 * satisfy this via an adapter (`objectTarget`/`emitterTarget`). Only objects with
 * size params set `supportsScale`; for them `scaleHandleLocals` returns the local
 * points the size handles ride (one per handle, index-aligned with the `scale`
 * GizmoHandle), recomputed live as the body scales.
 */
export interface GizmoTarget {
  readonly position: THREE.Vector3;
  readonly rotation: THREE.Quaternion;
  boundingRadius(): number;
  readonly supportsScale: boolean;
  readonly showMoveRotate: boolean;
  scaleHandleLocals?: (is3D: boolean) => THREE.Vector3[];
  railPointLocals?: () => THREE.Vector3[];
  rollAxis?: () => THREE.Vector3;
}

/**
 * World-space long axis of a rail: the start→end chord of its control points
 * carried through the live rotation. A degenerate chord (coincident endpoints)
 * falls back to local +X so the roll ring stays usable.
 */
function railRollAxis(obj: PlacedObject): THREE.Vector3 {
  const first = obj.railPoints[0];
  const last = obj.railPoints[obj.railPoints.length - 1];
  const axis = new THREE.Vector3(last.x - first.x, last.y - first.y, 0);
  if (axis.lengthSq() < 1e-8) {
    axis.set(1, 0, 0);
  }
  return axis.normalize().applyQuaternion(obj.rotation);
}

export function objectTarget(obj: PlacedObject, groupBoundingRadius?: () => number): GizmoTarget {
  return {
    position: obj.position,
    rotation: obj.rotation,
    boundingRadius: groupBoundingRadius ?? ((): number => boundingRadius(obj)),
    supportsScale: obj.type === "box" || obj.type === "sphere" || obj.type === "funnel" || obj.type === "button" || obj.type === "lamp" || obj.type === "knob" || obj.type === "array" || obj.type === "curve" || obj.type === "display",
    showMoveRotate: isCollider(obj.type) || obj.type === "orbit" || obj.type === "group",
    scaleHandleLocals: (is3D: boolean): THREE.Vector3[] => handleLocals(obj, is3D),
    railPointLocals: obj.type === "rail"
      ? (): THREE.Vector3[] => obj.railPoints.map((p: THREE.Vector2): THREE.Vector3 => new THREE.Vector3(p.x, p.y, 0))
      : undefined,
    rollAxis: obj.type === "rail" ? (): THREE.Vector3 => railRollAxis(obj) : undefined,
  };
}

export function groupBoundingRadiusFromMesh(groupOrigin: THREE.Vector3, groupMesh: THREE.Object3D | null): number {
  if (!groupMesh) {
    return 0.6;
  }
  const offset: number = groupMesh.position.distanceTo(groupOrigin);
  const halfDiagonal: number = 0.5 * Math.hypot(groupMesh.scale.x, groupMesh.scale.y, groupMesh.scale.z);
  return offset + halfDiagonal;
}

export function emitterTarget(em: Emitter): GizmoTarget {
  return {
    position: em.position,
    rotation: em.rotation,
    boundingRadius: (): number => EMITTER_GIZMO_BOUND,
    supportsScale: false,
    showMoveRotate: true,
  };
}

function widgetBaseRadius(obj: PlacedObject): number {
  if (obj.type === "lamp") {
    return LAMP_BASE_RADIUS;
  }
  if (obj.type === "knob") {
    return KNOB_BASE_RADIUS;
  }
  return BUTTON_BASE_RADIUS;
}

/**
 * Local-space points of the visible shell (params = visible size) where the
 * size handles sit, index-aligned with the `scale` GizmoHandle. Box: a handle on
 * the +Z face's top-right and bottom-left corners (same two in 2D and 3D), each
 * driving a diagonal-fixed resize. Sphere: one handle on the radius edge in the +X+Y
 * (45°) direction, clear of the rotation rings. Funnel: three handles — the bottom rim's outer corner and the
 * top rim's outer corner set each rim radius independently, and a third on the
 * top rim's inner edge sets the wall thickness; it sits on the -X side so it
 * stays clear of the nearby top-radius handle. Button / lamp / knob: one handle
 * on the top-right corner of the widget's bounding square (baseRadius × uniform
 * scale on each axis), matching the other widgets' corner grips and driving a
 * circle-preserving uniform resize.
 * Array / curve / envelope: the same two diagonal corners as the box (top-right
 * and bottom-left of the widget's outer extent), each driving a diagonal-fixed
 * resize via SeqCornerResizeDrag. Handles are grabbed on a screen-parallel
 * plane, so 2D and 3D drags share these points (3D "Front view" behavior in both).
 */
function handleLocals(obj: PlacedObject, is3D: boolean): THREE.Vector3[] {
  const p = obj.params;
  if (obj.type === "box") {
    return boxCornerSigns(is3D).map(([sx, sy, sz]: readonly [number, number, number]): THREE.Vector3 =>
      new THREE.Vector3(
        sx * p.x,
        sy * p.y,
        sz === 0 ? p.z : sz * p.z,
      ));
  }
  if (obj.type === "sphere") {
    return [new THREE.Vector3(p.x * Math.SQRT1_2, p.x * Math.SQRT1_2, 0)];
  }
  if (obj.type === "button" || obj.type === "lamp" || obj.type === "knob") {
    const r = widgetBaseRadius(obj) * widgetScale(obj);
    return [new THREE.Vector3(r, r, 0)];
  }
  if (obj.type === "array" || obj.type === "curve" || obj.type === "envelope" || obj.type === "pitchQuantize" || obj.type === "display") {
    const half = seqOuterHalf(obj);
    return SEQ_CORNER_SIGNS_2D.map(([sx, sy]: readonly [number, number]): THREE.Vector3 =>
      new THREE.Vector3(sx * half.hw, sy * half.hh, 0));
  }
  return [
    new THREE.Vector3(p.x + p.w, -p.z, 0),
    new THREE.Vector3(p.y + p.w, p.z, 0),
    new THREE.Vector3(-(p.y - p.w), p.z, 0),
  ];
}

/**
 * Maps a cursor point expressed in the object's local frame to new size params
 * for its type, for the grabbed handle index. Sphere reads the radius as the
 * in-plane distance to the cursor (its handle rides the 45° +X+Y radius edge);
 * funnel sets the
 * grabbed rim's radius (handle 0 = bottom, 1 = top) and the half-height —
 * both rim handles drive the height, keeping the funnel center-symmetric
 * while the radii stay independent — and handle 2 (the top rim's inner edge)
 * sets only the wall thickness, clamped so the inner opening never closes or
 * crosses to the far side. The drag plane is screen-parallel through the
 * handle, so a front-on view holds the depth axis steady on its own. Button /
 * lamp / knob read a single uniform scale off the cursor's larger axis distance
 * over the base radius (their handle rides the bounding square's corner, so the
 * larger axis lands on the grabbed corner without a jump), clamped to
 * [WIDGET_SCALE_MIN, WIDGET_SCALE_MAX], stored in
 * params.x. Box and the seq / envelope widgets do not pass through here — their
 * corner handles resize diagonal-fixed via BoxCornerResizeDrag /
 * SeqCornerResizeDrag.
 */
export function scaleParamsFromLocal(obj: PlacedObject, lp: THREE.Vector3, handle: number, grid?: number): THREE.Vector4 {
  const snapDim = (value: number): number => (grid === undefined ? value : quantize(value, grid));
  if (obj.type === "sphere") {
    const r = Math.max(MIN_HALF, snapDim(Math.hypot(lp.x, lp.y)));
    return new THREE.Vector4(r, 0, 0, 0);
  }
  if (obj.type === "button" || obj.type === "lamp" || obj.type === "knob") {
    const reach = Math.max(Math.abs(lp.x), Math.abs(lp.y));
    const s = Math.min(Math.max(Math.round(reach / widgetBaseRadius(obj)), 1), WIDGET_MAX_UNITS);
    return new THREE.Vector4(s, 0, 0, 0);
  }
  const p = obj.params;
  if (handle === 2) {
    const wMax = Math.max(MIN_WALL_HALF, p.y - MIN_WALL_HALF);
    const w = Math.min(wMax, Math.max(MIN_WALL_HALF, snapDim(p.y - Math.abs(lp.x))));
    return new THREE.Vector4(p.x, p.y, p.z, w);
  }
  const r = Math.max(MIN_HALF, snapDim(Math.abs(lp.x) - p.w));
  const h = Math.max(MIN_HALF, snapDim(Math.abs(lp.y)));
  return handle === 1
    ? new THREE.Vector4(p.x, r, h, p.w)
    : new THREE.Vector4(r, p.y, h, p.w);
}

/**
 * Circumscribed radius of the object's local AABB, used to place the rotation
 * rings just outside the body. Sphere uses its radius; box the corner distance;
 * funnel the outer rim distance.
 */
function boundingRadius(obj: PlacedObject): number {
  const p = obj.params;
  if (obj.type === "box") {
    return Math.hypot(p.x, p.y, p.z);
  }
  if (obj.type === "sphere") {
    return p.x;
  }
  if (obj.type === "force") {
    return p.x;
  }
  if (obj.type === "rail") {
    let maxR = 0;
    for (const pt of obj.railPoints) {
      maxR = Math.max(maxR, pt.length());
    }
    return maxR + 0.6;
  }
  if (obj.type === "button" || obj.type === "lamp" || obj.type === "knob") {
    return widgetBaseRadius(obj) * widgetScale(obj);
  }
  if (obj.type === "array" || obj.type === "curve") {
    const base = seqBaseHalfExtent(obj);
    const s = seqScale(obj);
    return Math.hypot(base.w * s.x + SEQ_FRAME_W, base.h * s.y + SEQ_FRAME_W);
  }
  if (obj.type === "display") {
    const half = displayPanelHalfExtent(obj);
    return Math.hypot(half.hw, half.hh);
  }
  if (obj.type === "envelope") {
    return Math.hypot(ENVELOPE_BASE_HALF.x, ENVELOPE_BASE_HALF.y) + 0.2;
  }
  if (!isCollider(obj.type)) {
    return 0.6;
  }
  return Math.hypot(Math.max(p.x, p.y) + p.w, p.z);
}

interface RingElem {
  axis: RingAxis;
  axisVec: THREE.Vector3;
  u: THREE.Vector3;
  v: THREE.Vector3;
  solid: THREE.Mesh;
  stubSolid: THREE.Mesh;
  stubPick: THREE.Mesh;
}

/**
 * In-plane basis (u, v) perpendicular to an arbitrary ring axis, the same
 * reference-pick rule RotateDrag uses so the ring and its drag share a frame.
 */
function ringBasis(axisVec: THREE.Vector3): { u: THREE.Vector3; v: THREE.Vector3 } {
  const ref = Math.abs(axisVec.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
  const u = new THREE.Vector3().crossVectors(ref, axisVec).normalize();
  const v = new THREE.Vector3().crossVectors(axisVec, u).normalize();
  return { u, v };
}

/**
 * The default rotation affordance shown in place of a full ring: a short solid
 * arc of the ring at its bottom-right 45° point plus an invisible box that is the
 * raycast grab target. Hover/click only hit this arc, never the rest of the
 * (hidden) ring, so the pick box hugs the arc: its long axis runs along the
 * chord between the arc ends. The full ring is revealed only while this stub is
 * hovered, mirroring the scale-handle wire→solid idiom (here stub → full ring).
 * `orientStub` places both in the ring's (u, v) plane.
 */
function buildStub(axis: RingAxis, radius: number): { solid: THREE.Mesh; pick: THREE.Mesh } {
  const solid = new THREE.Mesh(
    new THREE.TorusGeometry(radius, RING_TUBE, 8, 12, STUB_ARC),
    new THREE.MeshBasicMaterial({ color: AXIS_COLOR[axis], depthTest: false }),
  );
  solid.renderOrder = 1000;
  const chord = 2 * radius * Math.sin(STUB_ARC / 2) + STUB_PICK_MARGIN;
  const pick = new THREE.Mesh(
    new THREE.BoxGeometry(chord, STUB_PICK_THICK, STUB_PICK_THICK),
    new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }),
  );
  pick.userData.rotateAxis = axis;
  return { solid, pick };
}

/**
 * Places the stub arc and its pick box at STUB_ANGLE on the ring circle within
 * the ring's (u, v) plane: the arc is spun so its center sits at that angle, and
 * the pick box rides the matching circumference point with its long axis aligned
 * to the arc's tangent so the grab region tracks the visible arc.
 */
function orientStub(elem: RingElem, radius: number): void {
  const c = Math.cos(STUB_ANGLE);
  const s = Math.sin(STUB_ANGLE);
  const radial = new THREE.Vector3(
    elem.u.x * c + elem.v.x * s,
    elem.u.y * c + elem.v.y * s,
    elem.u.z * c + elem.v.z * s,
  );
  const tangent = new THREE.Vector3(
    -elem.u.x * s + elem.v.x * c,
    -elem.u.y * s + elem.v.y * c,
    -elem.u.z * s + elem.v.z * c,
  );
  elem.stubPick.position.copy(radial).multiplyScalar(radius);
  elem.stubPick.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(tangent, radial, elem.axisVec));
  const basis = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(elem.u, elem.v, elem.axisVec));
  const spin = new THREE.Quaternion().setFromAxisAngle(AXIS_UNIT.z, STUB_ANGLE - STUB_ARC / 2);
  elem.stubSolid.quaternion.copy(basis.multiply(spin));
}

/** Aligns a full ring torus (local plane XY about Z) with the ring's (u, v) world plane. */
function orientRingSolid(elem: RingElem): void {
  elem.solid.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(elem.u, elem.v, elem.axisVec));
}

function buildRingSolid(axis: RingAxis, radius: number): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.TorusGeometry(radius, RING_TUBE, 8, 64, Math.PI * 2),
    new THREE.MeshBasicMaterial({ color: AXIS_COLOR[axis], depthTest: false }),
  );
  mesh.renderOrder = 1000;
  mesh.visible = false;
  return mesh;
}

/**
 * World-axis-aligned rotation rings for the selected object: one z ring in 2D
 * (face-in-plane), three RGB rings (x=red / y=green / z=blue) in 3D. Rings follow
 * the object's position but never its orientation, so each presents the clean
 * world-axis plane the drag rotates about (premultiply). Each ring shows only a
 * short 45° stub arc by default; hovering the stub reveals the full circle (a
 * 360° torus in both 2D and 3D). Scale and rail-point handles are DOM overlays
 * (see the caller); this class only supplies their live world positions.
 */
export class Gizmo {
  readonly group: THREE.Group;
  private disposables: THREE.Object3D[];
  private rings: RingElem[];
  private scaleHandleCountValue: number;
  private railPointCountValue: number;
  private target: GizmoTarget | null;
  private ringRadiusValue: number;
  private is2D: boolean;
  private hover: GizmoHandle | null;
  private readonly resolution: THREE.Vector2;

  constructor() {
    this.group = new THREE.Group();
    this.group.visible = false;
    this.disposables = [];
    this.rings = [];
    this.scaleHandleCountValue = 0;
    this.railPointCountValue = 0;
    this.target = null;
    this.ringRadiusValue = 0;
    this.is2D = true;
    this.hover = null;
    this.resolution = new THREE.Vector2(1, 1);
  }

  attach(target: GizmoTarget, is3D: boolean): void {
    this.detach();
    this.target = target;
    this.is2D = !is3D;
    this.ringRadiusValue = target.boundingRadius() + RING_MARGIN;
    const axes: Axis[] = !target.showMoveRotate ? [] : is3D ? ["x", "y", "z"] : ["z"];
    for (const axis of axes) {
      const solid = buildRingSolid(axis, this.ringRadiusValue);
      const stub = buildStub(axis, this.ringRadiusValue);
      const elem: RingElem = { axis, axisVec: AXIS_UNIT[axis], u: AXIS_UV[axis].u, v: AXIS_UV[axis].v, solid, stubSolid: stub.solid, stubPick: stub.pick };
      orientRingSolid(elem);
      orientStub(elem, this.ringRadiusValue);
      this.rings.push(elem);
      this.disposables.push(solid, stub.solid, stub.pick);
      this.group.add(solid, stub.solid, stub.pick);
    }
    if (is3D && target.rollAxis) {
      const axisVec = target.rollAxis();
      const { u, v } = ringBasis(axisVec);
      const solid = buildRingSolid("roll", this.ringRadiusValue);
      const stub = buildStub("roll", this.ringRadiusValue);
      const elem: RingElem = { axis: "roll", axisVec, u, v, solid, stubSolid: stub.solid, stubPick: stub.pick };
      orientRingSolid(elem);
      orientStub(elem, this.ringRadiusValue);
      this.rings.push(elem);
      this.disposables.push(solid, stub.solid, stub.pick);
      this.group.add(solid, stub.solid, stub.pick);
    }
    this.scaleHandleCountValue = target.supportsScale && target.scaleHandleLocals ? target.scaleHandleLocals(is3D).length : 0;
    this.railPointCountValue = target.railPointLocals ? target.railPointLocals().length : 0;
    this.group.visible = true;
    applyFatLineResolution(this.group, this.resolution.x, this.resolution.y);
    this.sync();
  }

  /**
   * Keeps the fat-line width correct in pixels after a canvas resize: stored for
   * the next attach's rebuild and applied to the lines currently in the group.
   */
  setResolution(width: number, height: number): void {
    this.resolution.set(width, height);
    applyFatLineResolution(this.group, width, height);
  }

  detach(): void {
    for (const obj of this.disposables) {
      (obj as THREE.Mesh).geometry.dispose();
      ((obj as THREE.Mesh).material as THREE.Material).dispose();
    }
    this.group.clear();
    this.disposables = [];
    this.rings = [];
    this.scaleHandleCountValue = 0;
    this.railPointCountValue = 0;
    this.target = null;
    this.hover = null;
    this.group.visible = false;
  }

  setHover(handle: GizmoHandle | null): void {
    this.hover = handle;
  }

  hasTarget(): boolean {
    return this.target !== null;
  }

  /**
   * Outer reach of the gizmo. Panels anchor outside this so the rotation ring
   * and scale/rail handles stay clickable instead of being covered.
   */
  handleReach(): number {
    return this.ringRadiusValue + HANDLE_HALF;
  }

  ringRadius(): number {
    return this.ringRadiusValue;
  }

  sync(): void {
    if (!this.target) {
      return;
    }
    this.group.position.copy(this.target.position);
  }

  /**
   * Per-frame visual pass: shows the hovered ring as a solid torus and, in 3D,
   * re-banks the live roll ring. Scale and rail-point handles are DOM overlays,
   * so their hover/visibility is handled by the caller, not here.
   */
  orient(): void {
    if (!this.target) {
      return;
    }
    for (const elem of this.rings) {
      const hovered = this.hover?.kind === "rotate" && this.hover.axis === elem.axis;
      elem.solid.visible = hovered;
      if (elem.axis === "roll" && this.target.rollAxis) {
        elem.axisVec = this.target.rollAxis();
        const { u, v } = ringBasis(elem.axisVec);
        elem.u = u;
        elem.v = v;
        orientRingSolid(elem);
        orientStub(elem, this.ringRadiusValue);
      }
    }
  }

  scaleHandleCount(): number {
    return this.scaleHandleCountValue;
  }

  /**
   * World position of scale handle `index`, computed live from the target's
   * current size so DOM handles and their drag plane track a resize in progress.
   * The handles are DOM overlays projected by the caller, not scene meshes.
   */
  scaleHandleWorld(index: number): THREE.Vector3 | null {
    if (!this.target || !this.target.scaleHandleLocals || index < 0 || index >= this.scaleHandleCountValue) {
      return null;
    }
    const local = this.target.scaleHandleLocals(!this.is2D)[index];
    if (!local) {
      return null;
    }
    return local.clone().applyQuaternion(this.target.rotation).add(this.target.position);
  }

  railPointCount(): number {
    return this.railPointCountValue;
  }

  /**
   * Live world position of rail control point `index`, recomputed from the
   * target's current points so a point being dragged (or added / removed) tracks
   * in step. Rail-point handles are DOM overlays projected by the caller.
   */
  railPointWorld(index: number): THREE.Vector3 | null {
    if (!this.target || !this.target.railPointLocals || index < 0 || index >= this.railPointCountValue) {
      return null;
    }
    const local = this.target.railPointLocals()[index];
    if (!local) {
      return null;
    }
    return local.clone().applyQuaternion(this.target.rotation).add(this.target.position);
  }

  /**
   * Resolves which gizmo handle the ray hits: the rotation stub arc, grabbed only
   * via the stub's arc-hugging pick box — never the rest of the (hidden) ring.
   * Scale and rail-point handles are DOM overlays that take their own pointer
   * events, so they are not raycast here.
   */
  pick(raycaster: THREE.Raycaster): GizmoHandle | null {
    if (this.rings.length === 0) {
      return null;
    }
    const stubPicks = this.rings.map((r: RingElem): THREE.Mesh => r.stubPick);
    const stubHits = raycaster.intersectObjects(stubPicks, false);
    if (stubHits.length > 0) {
      return { kind: "rotate", axis: stubHits[0].object.userData.rotateAxis as RingAxis };
    }
    return null;
  }

  /**
   * World axis vector of a rotation ring, resolving "roll" to the target's live
   * roll axis. RotateDrag captures this at grab time.
   */
  rotateAxisVec(axis: RingAxis): THREE.Vector3 | null {
    const elem = this.rings.find((r: RingElem): boolean => r.axis === axis);
    return elem ? elem.axisVec.clone() : null;
  }
}

/**
 * Signed twist angle of `q` about the unit `axis` (swing-twist decomposition).
 * The twist quaternion is the normalized projection (dot(q.xyz, axis)·axis, q.w);
 * its angle is the same whether the decomposition is written swing·twist or
 * twist·swing, so callers need not pick a convention. A pure 180° swing
 * (projection ≈ 0) has no defined twist and returns 0.
 */
export function twistAngleAbout(q: THREE.Quaternion, axis: THREE.Vector3): number {
  const d = q.x * axis.x + q.y * axis.y + q.z * axis.z;
  if (Math.abs(d) < 1e-9 && Math.abs(q.w) < 1e-9) {
    return 0;
  }
  return 2 * Math.atan2(d, q.w);
}

/**
 * Parameters of one rotate-drag's 15° snap grid. The grid stays delta-from-grab
 * (snap and free drag never disagree by more than half a step) but is phase-
 * aligned so the reachable snapped orientations are whole-step multiples of the
 * subject's axis twist, which puts identity (zero rotation) on a snap dot.
 */
export interface RotateSnapSpec {
  stepRad: number;
  dotRadius: number;
  threshold: number;
}

export function rotateSnapSpec(ringRadius: number, stepRad: number = SNAP_STEP_RAD): RotateSnapSpec {
  const dotRadius = ringRadius * SNAP_DOT_RADIUS_FACTOR;
  return {
    stepRad,
    dotRadius,
    threshold: dotRadius * Math.sin(stepRad / 2),
  };
}

export interface RotateGuideFrame {
  center: THREE.Vector3;
  axisVec: THREE.Vector3;
  u: THREE.Vector3;
  v: THREE.Vector3;
  downAngle: number;
  gridPhaseRad: number;
}

/**
 * Per-drag snap visuals: a reference line from the ring center out through the
 * grab direction, plus one dot per 15° grid step on a circle at twice the ring
 * radius. The dots sit at downAngle + gridPhase + k·step in the drag's (u, v)
 * plane — the phase aligns them to the subject's axis-twist grid (so one dot is
 * identity / zero rotation) while staying within half a step of the grab. The
 * caller owns scene add/remove; dispose() releases the geometry and materials.
 */
export class RotateSnapGuide {
  readonly group: THREE.Group;
  private readonly dots: THREE.Mesh[];
  private readonly dotMaterial: THREE.MeshBasicMaterial;
  private readonly activeMaterial: THREE.MeshBasicMaterial;
  private readonly lineMaterial: LineMaterial;
  private readonly dotGeometry: THREE.SphereGeometry;
  private readonly lineGeometry: LineGeometry;
  private activeIndex: number | null;

  constructor(axis: RingAxis, frame: RotateGuideFrame, spec: RotateSnapSpec) {
    this.group = new THREE.Group();
    this.group.position.copy(frame.center);
    const color = AXIS_COLOR[axis];
    const lineLen = (spec.dotRadius / SNAP_DOT_RADIUS_FACTOR) * SNAP_LINE_LEN_FACTOR;
    const grabDir = frame.u
      .clone()
      .multiplyScalar(Math.cos(frame.downAngle))
      .addScaledVector(frame.v, Math.sin(frame.downAngle));
    const tip = grabDir.multiplyScalar(lineLen);
    this.lineGeometry = new LineGeometry();
    this.lineGeometry.setPositions([0, 0, 0, tip.x, tip.y, tip.z]);
    this.lineMaterial = fatLineMaterial(color);
    this.lineMaterial.resolution.set(window.innerWidth, window.innerHeight);
    const line = new Line2(this.lineGeometry, this.lineMaterial);
    line.renderOrder = 999;
    this.group.add(line);
    this.dotGeometry = new THREE.SphereGeometry(SNAP_DOT_SIZE, 12, 8);
    this.dotMaterial = new THREE.MeshBasicMaterial({ color, depthTest: false });
    this.activeMaterial = new THREE.MeshBasicMaterial({ color: SNAP_DOT_ACTIVE_COLOR, depthTest: false });
    this.dots = [];
    const dotCount = Math.round((Math.PI * 2) / spec.stepRad);
    for (let k = 0; k < dotCount; k++) {
      const angle = frame.downAngle + frame.gridPhaseRad + k * spec.stepRad;
      const dot = new THREE.Mesh(this.dotGeometry, this.dotMaterial);
      dot.position
        .copy(frame.u)
        .multiplyScalar(Math.cos(angle))
        .addScaledVector(frame.v, Math.sin(angle))
        .multiplyScalar(spec.dotRadius);
      dot.renderOrder = 1000;
      this.dots.push(dot);
      this.group.add(dot);
    }
    this.activeIndex = null;
  }

  setActive(index: number | null): void {
    if (index === this.activeIndex) {
      return;
    }
    if (this.activeIndex !== null) {
      const prev = this.dots[this.activeIndex];
      prev.material = this.dotMaterial;
      prev.scale.setScalar(1);
    }
    if (index !== null && this.dots[index]) {
      const next = this.dots[index];
      next.material = this.activeMaterial;
      next.scale.setScalar(SNAP_DOT_ACTIVE_SCALE);
    }
    this.activeIndex = index;
  }

  dispose(): void {
    this.lineGeometry.dispose();
    this.dotGeometry.dispose();
    this.lineMaterial.dispose();
    this.dotMaterial.dispose();
    this.activeMaterial.dispose();
  }
}

/**
 * One rotation-ring drag gesture. Captures the object's orientation at grab time
 * and rotates it by the angle the cursor sweeps within the ring's world-axis
 * plane. The increment is a world-space rotation (premultiplied onto the start
 * quaternion), so gimbal order never matters.
 */
export class RotateDrag {
  private readonly center: THREE.Vector3;
  private readonly axisVec: THREE.Vector3;
  private readonly plane: THREE.Plane;
  private readonly u: THREE.Vector3;
  private readonly v: THREE.Vector3;
  private readonly startQuat: THREE.Quaternion;
  private readonly hitPoint: THREE.Vector3;
  private readonly downAngle: number;
  private readonly snap: RotateSnapSpec | null;
  private readonly gridPhase: number;
  private activeSnap: number | null;

  constructor(axisVec: THREE.Vector3, center: THREE.Vector3, startQuat: THREE.Quaternion, ray: THREE.Ray, snap?: RotateSnapSpec) {
    this.center = center.clone();
    this.axisVec = axisVec.clone();
    this.startQuat = startQuat.clone();
    this.plane = new THREE.Plane().setFromNormalAndCoplanarPoint(this.axisVec, this.center);
    const ref = Math.abs(this.axisVec.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
    this.u = new THREE.Vector3().crossVectors(ref, this.axisVec).normalize();
    this.v = new THREE.Vector3().crossVectors(this.axisVec, this.u).normalize();
    this.hitPoint = new THREE.Vector3();
    this.downAngle = this.angleAt(ray) ?? 0;
    this.snap = snap ?? null;
    this.gridPhase = -twistAngleAbout(this.startQuat, this.axisVec);
    this.activeSnap = null;
  }

  private angleAt(ray: THREE.Ray): number | null {
    if (!ray.intersectPlane(this.plane, this.hitPoint)) {
      return null;
    }
    const dx = this.hitPoint.dot(this.u) - this.center.dot(this.u);
    const dy = this.hitPoint.dot(this.v) - this.center.dot(this.v);
    return Math.atan2(dy, dx);
  }

  /**
   * Snapped rotation when the cursor sits within `threshold` of the nearest grid
   * dot, free delta rotation otherwise. Both branches premultiply
   * Rot(axis, delta) onto the start quaternion; snap quantizes delta to
   * gridPhase + steps·step, where gridPhase = −startTwist makes the resulting
   * axis twist a whole multiple of step (so identity is reachable) while keeping
   * the snapped delta within half a step of the free delta.
   */
  update(ray: THREE.Ray): THREE.Quaternion | null {
    const angle = this.angleAt(ray);
    if (angle === null) {
      return null;
    }
    let delta = angle - this.downAngle;
    if (this.snap) {
      const steps = this.snapSteps(this.snap);
      if (steps !== null) {
        const dotCount = Math.round((Math.PI * 2) / this.snap.stepRad);
        this.activeSnap = ((steps % dotCount) + dotCount) % dotCount;
        delta = this.gridPhase + steps * this.snap.stepRad;
        return new THREE.Quaternion().setFromAxisAngle(this.axisVec, delta).multiply(this.startQuat);
      }
    }
    this.activeSnap = null;
    return new THREE.Quaternion().setFromAxisAngle(this.axisVec, delta).multiply(this.startQuat);
  }

  /**
   * Step index (signed) of the grid dot the cursor is within `threshold` of, null
   * otherwise. Dots sit at downAngle + gridPhase + steps·step on the ring, so the
   * magnetic test mirrors the phase-aligned grid the guide draws.
   */
  private snapSteps(snap: RotateSnapSpec): number | null {
    const dx = this.hitPoint.dot(this.u) - this.center.dot(this.u);
    const dy = this.hitPoint.dot(this.v) - this.center.dot(this.v);
    const hitAngle = Math.atan2(dy, dx);
    const steps = Math.round((hitAngle - this.downAngle - this.gridPhase) / snap.stepRad);
    const dotAngle = this.downAngle + this.gridPhase + steps * snap.stepRad;
    const dot = this.center
      .clone()
      .addScaledVector(this.u, Math.cos(dotAngle) * snap.dotRadius)
      .addScaledVector(this.v, Math.sin(dotAngle) * snap.dotRadius);
    return this.hitPoint.distanceTo(dot) <= snap.threshold ? steps : null;
  }

  activeSnapIndex(): number | null {
    return this.activeSnap;
  }

  guideFrame(): RotateGuideFrame {
    return {
      center: this.center.clone(),
      axisVec: this.axisVec.clone(),
      u: this.u.clone(),
      v: this.v.clone(),
      downAngle: this.downAngle,
      gridPhaseRad: this.gridPhase,
    };
  }
}

/**
 * One box corner-resize drag gesture: the grabbed corner follows the cursor
 * while the diagonally opposite corner stays fixed, so half-extents and center
 * update together. The opposite corner and the orientation are captured in
 * world space at grab time and all sizing math runs in that local frame, so
 * rotated boxes resize along their own axes; only the resulting center returns
 * to world space. A zero z corner sign (2D handle) leaves the depth half-extent
 * and the center's local-Z untouched. Half-extents clamp at MIN_HALF, pinning
 * the box at the fixed corner when the cursor crosses the diagonal instead of
 * inverting the geometry.
 */
export class BoxCornerResizeDrag {
  private readonly signs: readonly [number, number, number];
  private readonly rotation: THREE.Quaternion;
  private readonly invRotation: THREE.Quaternion;
  private readonly fixedWorld: THREE.Vector3;
  private readonly is2D: boolean;
  private readonly startHalfZ: number;
  private readonly startW: number;

  constructor(obj: PlacedObject, handleIndex: number, is3D: boolean) {
    this.signs = boxCornerSigns(is3D)[handleIndex];
    this.rotation = obj.rotation.clone();
    this.invRotation = this.rotation.clone().conjugate();
    this.is2D = !is3D;
    this.startHalfZ = obj.params.z;
    this.startW = obj.params.w;
    const [sx, sy, sz] = this.signs;
    const fixedLocal = new THREE.Vector3(-sx * obj.params.x, -sy * obj.params.y, -sz * obj.params.z);
    this.fixedWorld = fixedLocal.applyQuaternion(this.rotation).add(obj.position);
  }

  /**
   * Quantizing the fixed corner and the dragged corner in world space (before
   * the local-frame sizing math) lands both ends on the grid, which makes the
   * half-extents fall out as grid multiples for axis-aligned boxes; a null grid
   * keeps the free resize.
   */
  update(point: THREE.Vector3, grid?: number): { params: THREE.Vector4; position: THREE.Vector3 } {
    const [sx, sy, sz] = this.signs;
    const fixed = grid === undefined ? this.fixedWorld.clone() : snapPos(this.fixedWorld, grid, this.is2D);
    const corner = grid === undefined ? point.clone() : snapPos(point, grid, this.is2D);
    const d = corner.clone().sub(fixed).applyQuaternion(this.invRotation);
    const hx = Math.max(MIN_HALF, (sx * d.x) / 2);
    const hy = Math.max(MIN_HALF, (sy * d.y) / 2);
    const hz = sz === 0 ? this.startHalfZ : Math.max(MIN_HALF, (sz * d.z) / 2);
    const centerOffset = new THREE.Vector3(sx * hx, sy * hy, sz * hz).applyQuaternion(this.rotation);
    return {
      params: new THREE.Vector4(hx, hy, hz, this.startW),
      position: fixed.clone().add(centerOffset),
    };
  }
}

/**
 * One corner-resize drag for a planar seq / envelope widget: the grabbed corner
 * follows the cursor while the diagonally opposite corner stays fixed, so the
 * scale and center move together (the same diagonal-fixed model as the box, but
 * its result maps to the widget's scale storage instead of half-extent params).
 * The opposite corner is captured in world space at grab; all sizing runs in the
 * widget's local frame so a rotated widget resizes along its own axes. Both ends
 * snap to the grid when one is given. Scale clamps via seqScalePatch.
 */
export class SeqCornerResizeDrag {
  private readonly signs: readonly [number, number];
  private readonly rotation: THREE.Quaternion;
  private readonly invRotation: THREE.Quaternion;
  private readonly fixedWorld: THREE.Vector3;
  private readonly obj: PlacedObject;

  constructor(obj: PlacedObject, handleIndex: number) {
    this.obj = obj;
    this.signs = SEQ_CORNER_SIGNS_2D[handleIndex];
    this.rotation = obj.rotation.clone();
    this.invRotation = this.rotation.clone().conjugate();
    const { hw, hh } = seqOuterHalf(obj);
    const [sx, sy] = this.signs;
    const fixedLocal = new THREE.Vector3(-sx * hw, -sy * hh, 0);
    this.fixedWorld = fixedLocal.applyQuaternion(this.rotation).add(obj.position);
  }

  update(point: THREE.Vector3, grid?: number): { patch: { params?: THREE.Vector4; scale?: THREE.Vector2 }; position: THREE.Vector3 } {
    const [sx, sy] = this.signs;
    const fixed = grid === undefined ? this.fixedWorld.clone() : snapPos(this.fixedWorld, grid, true);
    const corner = grid === undefined ? point.clone() : snapPos(point, grid, true);
    const d = corner.clone().sub(fixed).applyQuaternion(this.invRotation);
    const hw = Math.max(MIN_HALF, (sx * d.x) / 2);
    const hh = Math.max(MIN_HALF, (sy * d.y) / 2);
    const patch = seqScalePatch(this.obj, hw, hh);
    const realized = realizedHalf(this.obj, patch);
    const centerOffset = new THREE.Vector3(sx * realized.hw, sy * realized.hh, 0).applyQuaternion(this.rotation);
    return { patch, position: fixed.clone().add(centerOffset) };
  }
}
